/**
 * Pure selector functions for cart state
 */

import type { CartState, CartItem, ProductId, PolicyId } from './cart-types'
import { createCartItemKey } from './cart-types'

/**
 * Calculate total quantity of all items in cart
 */
export const selectTotalQty = (state: CartState): number =>
  Object.values(state.items)
    .filter(item => !item.isGreenpoolProduct)
    .reduce((total, item) => total + item.qty, 0)
/**
 * Calculate subtotal in NOK (excludes inactive products)
 */
export const selectSubtotal = (state: CartState): number =>
  Object.values(state.items)
    .filter(item => !item.isGreenpoolProduct)
    .reduce((total, item) => {
      if (!item.product) {
        return total
      }
      return total + item.qty * item.product.price
    }, 0)

/**
 * Calculate line subtotal for a specific cart item
 */
export const selectLineSubtotal = (item: CartItem): number => {
  if (!item.product || item.isGreenpoolProduct) {
    return 0
  }
  return item.qty * item.product.price
}

/**
 * Get cart items as sorted array (by policy name, then product name)
 */
export const selectItemsArray = (state: CartState): CartItem[] =>
  Object.values(state.items).sort((a, b) => {
    // First sort by policy name if available
    if (a.policy?.name && b.policy?.name) {
      const policyCompare = a.policy.name.localeCompare(b.policy.name)
      if (policyCompare !== 0) return policyCompare
    }

    // Then sort by product name if both have products loaded
    if (a.product?.name && b.product?.name) {
      return a.product.name.localeCompare(b.product.name)
    }

    // If only one has product loaded, prioritize it
    if (a.product?.name && !b.product?.name) return -1
    if (!a.product?.name && b.product?.name) return 1

    // Fall back to ID sorting
    return String(a.productId).localeCompare(String(b.productId))
  })

/**
 * Get items as a map for O(1) lookups
 */
export const selectItemsMap = (state: CartState): Record<string, CartItem> => state.items

/**
 * Check if cart has a specific product from a specific policy
 */
export const selectHasItem = (
  state: CartState,
  policyId: PolicyId,
  productId: ProductId
): boolean => {
  const key = createCartItemKey(policyId, productId)
  return key in state.items
}

/**
 * Get quantity of a specific product from a specific policy (0 if not in cart)
 */
export const selectItemQty = (
  state: CartState,
  policyId: PolicyId,
  productId: ProductId
): number => {
  const key = createCartItemKey(policyId, productId)
  if (state.items[key]?.isGreenpoolProduct) {
    return 0
  }
  return state.items[key]?.qty ?? 0
}

/**
 * Get line subtotal for a specific product from a specific policy
 */
export const selectLineSubtotalById = (
  state: CartState,
  policyId: PolicyId,
  productId: ProductId
): number => {
  const key = createCartItemKey(policyId, productId)
  const item = state.items[key]
  return item ? selectLineSubtotal(item) : 0
}

/**
 * Get all unique product IDs in the cart (regardless of policy)
 */
export const selectProductIds = (state: CartState): ProductId[] => {
  const uniqueIds = new Set<ProductId>()
  Object.values(state.items).forEach(item => {
    uniqueIds.add(item.productId)
  })
  return Array.from(uniqueIds)
}

/**
 * Get all unique policy IDs in the cart
 */
export const selectPolicyIds = (state: CartState): PolicyId[] => {
  const uniqueIds = new Set<PolicyId>()
  Object.values(state.items).forEach(item => {
    uniqueIds.add(item.policyId)
  })
  return Array.from(uniqueIds)
}

/**
 * Get all unique policy IDs in the cart that do not have any greenpool products
 */
export const selectPolicyIdsWithoutGreenpool = (state: CartState): PolicyId[] => {
  const uniqueIds = new Set<PolicyId>()
  Object.values(state.items).forEach(item => {
    if (!item.isGreenpoolProduct) {
      uniqueIds.add(item.policyId)
    }
  })
  return Array.from(uniqueIds)
}

/**
 * Get all items for a specific policy
 */
export const selectItemsByPolicy = ({
  state,
  policyId,
  isSorted = true,
}: {
  state: CartState
  policyId: PolicyId
  isSorted?: boolean
}): CartItem[] => {
  const filtered = Object.values(state.items).filter(
    item => item.policyId === policyId && (!item.product?.isMandatory || true)
  )
  if (!isSorted) {
    return filtered
  }
  return filtered.sort((a, b) => {
    // Sort by product name if available
    if (a.product?.name && b.product?.name) {
      return a.product.name.localeCompare(b.product.name)
    }
    return String(a.productId).localeCompare(String(b.productId))
  })
}

export const groupItemsByPolicy = (state: CartState): Record<PolicyId, CartItem[]> => {
  const initialValue: Record<PolicyId, CartItem[]> = {}

  Object.values(state.items).reduce((acc, item) => {
    if (!acc[item.policyId]) {
      acc[item.policyId] = []
    }
    acc[item.policyId].push(item)
    return acc
  }, initialValue)

  return initialValue
}

export const groupNonMandatoryItemsByPolicy = (state: CartState): Record<PolicyId, CartItem[]> => {
  const initialValue: Record<PolicyId, CartItem[]> = {}

  Object.values(state.items).reduce((acc, item) => {
    if (!acc[item.policyId]) {
      acc[item.policyId] = []
    }
    if (!item.product?.isMandatory) {
      acc[item.policyId].push(item)
    }
    return acc
  }, initialValue)

  return initialValue
}

/**
 * Get subtotal for a specific policy
 */
export const selectPolicySubtotal = (state: CartState, policyId: PolicyId): number =>
  Object.values(state.items)
    .filter(item => item.policyId === policyId && !item.isGreenpoolProduct)
    .reduce((total, item) => {
      if (!item.product) {
        return total
      }
      return total + item.qty * item.product.price
    }, 0)

/**
 * Get total quantity for a specific policy
 */
export const selectPolicyQty = (state: CartState, policyId: PolicyId): number =>
  Object.values(state.items)
    .filter(item => item.policyId === policyId && !item.isGreenpoolProduct)
    .reduce((total, item) => total + item.qty, 0)

export const isItemInCart = (state: CartState, productId: ProductId): boolean =>
  Object.values(state.items).some(item => item.productId === productId)

export const getItemInCart = (state: CartState, productId: ProductId): CartItem | undefined =>
  Object.values(state.items).find(item => item.productId === productId)

/**
 * Get custom address from cart state
 */
export const getCustomAddressSelector = (state: CartState) =>
  state.customAddress ? state.customAddress : undefined

export const isGreenpoolItemInPolicy = (state: CartState, policyId: PolicyId): boolean =>
  Object.values(state.items)
    .filter(item => item.policyId === policyId)
    .some(item => item.isGreenpoolProduct)

export const isNonGreenpoolItemInPolicy = (state: CartState, policyId: PolicyId): boolean =>
  Object.values(state.items)
    .filter(item => item.policyId === policyId)
    .some(item => !item.isGreenpoolProduct)

export const selectGreenpoolItemInPolicy = (
  state: CartState,
  policyId: PolicyId
): CartItem | undefined =>
  Object.values(state.items)
    .filter(item => item.policyId === policyId)
    .find(item => item.isGreenpoolProduct)

export const groupGreenpoolItemsByPolicy = (state: CartState): Record<PolicyId, CartItem[]> => {
  const initialValue: Record<PolicyId, CartItem[]> = {}

  const grouped = Object.values(state.items).reduce((acc, item) => {
    const isGreenpool = item.isGreenpoolProduct
    if (!acc[item.policyId]) {
      acc[item.policyId] = []
    }
    if (isGreenpool) {
      acc[item.policyId].push(item)
    }
    return acc
  }, initialValue)

  // Filter out policies with empty lists
  return Object.fromEntries(Object.entries(grouped).filter(([, items]) => items.length > 0))
}

/**
 * Get greenpool cart items as sorted array (by policy name, then asset name)
 */
export const selectGreenpoolItemsArray = (state: CartState): CartItem[] =>
  Object.values(state.items)
    .filter(item => item.isGreenpoolProduct)
    .sort((a, b) => {
      // First sort by policy name if available
      if (a.policy?.name && b.policy?.name) {
        const policyCompare = a.policy.name.localeCompare(b.policy.name)
        if (policyCompare !== 0) return policyCompare
      }

      // Then sort by product name if both have products loaded
      if (a.asset?.assetName && b.asset?.assetName) {
        return a.asset.assetName.localeCompare(b.asset.assetName)
      }

      // If only one has product loaded, prioritize it
      if (a.asset?.assetName && !b.asset?.assetName) return -1
      if (!a.asset?.assetName && b.asset?.assetName) return 1

      // Fall back to ID sorting
      return String(a.productId).localeCompare(String(b.productId))
    })
